import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import type { RetainerAgreementWithFileDetails } from '@common/types/retainerAgreements';
import type { UserEntry } from '@common/types/users';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
    Alert,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { RETAINER_CHANGE_NOTE } from '@/pages/Settings/RetainerAgreement/constants';
import CreateRetainerModal from '@/pages/Settings/RetainerAgreement/CreateRetainerModal';

const NO_RETAINER = 'none';

interface Props {
    lawFirmId?: number;
    users: UserEntry[];
    practiceArea: ProspectPracticeArea | null;
    matterType: ProspectMatterType | null;
    /** All-users retainers for the template's practice area and matter type. Undefined until loaded */
    retainers: RetainerAgreementWithFileDetails[] | undefined;
    isLoadError: boolean;
    onRetryLoad(): void;
    value: number | null;
    onValueChange(retainerAgreementId: number | null): void;
    /** Everyone but Lexo Admins sees the section, but can't change it */
    disabled: boolean;
}

export default function TemplateRetainerSection({
    lawFirmId,
    users,
    practiceArea,
    matterType,
    retainers,
    isLoadError,
    onRetryLoad,
    value,
    onValueChange,
    disabled,
}: Props) {
    const [isUploadOpen, setIsUploadOpen] = useState(false);

    return (
        <Paper
            variant="outlined"
            sx={{ borderColor: 'neutral.borderColor', borderRadius: '8px', overflow: 'hidden' }}
        >
            <Stack
                sx={{
                    p: '14px 16px',
                    bgcolor: 'success.S_50',
                    borderBottom: '1px solid',
                    borderColor: 'neutral.borderColor',
                }}
            >
                <Typography variant="body2" fontWeight={600} sx={{ mb: '2px' }}>
                    Retainer Agreement/Fintrac Consent form
                </Typography>
                <Typography variant="caption" color="neutral.N_500">
                    Selected by default when creating a prospect from this template. Only documents
                    available to all users can be picked, so any assigned lawyer can send them.
                </Typography>
            </Stack>
            <Stack spacing={2} sx={{ p: 2 }}>
                {disabled && <Alert severity="info">{RETAINER_CHANGE_NOTE}</Alert>}
                {!retainers ? (
                    isLoadError ? (
                        <Alert
                            severity="error"
                            action={
                                <Button color="inherit" size="small" onClick={onRetryLoad}>
                                    Retry
                                </Button>
                            }
                        >
                            The firm's legal documents couldn't be loaded.
                        </Alert>
                    ) : (
                        <Stack alignItems="center" sx={{ padding: 2 }}>
                            <CircularProgress size={24} />
                        </Stack>
                    )
                ) : (
                    <>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                select
                                size="small"
                                label="Legal document"
                                value={value === null ? NO_RETAINER : String(value)}
                                onChange={(event) =>
                                    onValueChange(
                                        event.target.value === NO_RETAINER
                                            ? null
                                            : Number(event.target.value),
                                    )
                                }
                                disabled={disabled}
                                sx={{ flex: 1, maxWidth: 420 }}
                            >
                                <MenuItem value={NO_RETAINER}>None</MenuItem>
                                {retainers.map((retainer) => (
                                    <MenuItem
                                        key={retainer.retainerAgreementId}
                                        value={String(retainer.retainerAgreementId)}
                                    >
                                        {retainer.fileName}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileOutlinedIcon />}
                                onClick={() => setIsUploadOpen(true)}
                                disabled={disabled || !lawFirmId || !practiceArea || !matterType}
                            >
                                Upload new
                            </Button>
                        </Stack>
                        {!disabled && retainers.length === 0 && (
                            <Typography variant="caption" color="neutral.N_500">
                                The firm has no documents for this matter type yet. Uploaded ones
                                are added to Settings → Legal Documents for all users.
                            </Typography>
                        )}
                    </>
                )}
            </Stack>
            {isUploadOpen && lawFirmId && practiceArea && matterType && (
                <CreateRetainerModal
                    users={users}
                    lawFirmId={lawFirmId}
                    fixedMatter={{ practiceArea, matterType }}
                    onCreated={(retainer) => onValueChange(retainer.retainerAgreementId)}
                    onClose={() => setIsUploadOpen(false)}
                />
            )}
        </Paper>
    );
}
